import { useContext, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import useServer from '../../../api/useServerMain';
import useServerProduct from '../../../api/useServerProducts';
import CurrencyAmountInput from '../../../components/forms/CurrencyAmountInput';
import InlineRadio from '../../../components/forms/InlineRadio';
import TextArea from '../../../components/forms/TextArea';
import Toggle from '../../../components/forms/Toggle';
import AlertContainer from '../../../components/misc/AlertContainer';
import Button from '../../../components/misc/Button';
import GenericTable, { DataTableInterface } from '../../../components/misc/GenericTable';
import ImageComponent from '../../../components/misc/Images/Image';
import { BtnActions } from '../../../components/misc/MultipleActBtn';
import SpinnerLoading from '../../../components/misc/SpinnerLoading';
import Modal from '../../../components/modals/GenericModal';
import { ServerVariationInterface } from '../../../interfaces/ServerInterfaces';
import { useAppSelector } from '../../../store/hooks';
import { formatCurrency } from '../../../utils/helpers';
import { DetailProductContext } from '../DetailProductContainer';

//MAIN COMPONENT ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const Variations = () => {
  const { denyRoles } = useServer()
  const { product } = useContext(DetailProductContext)
  const [detailModalData, setDetailModalData] = useState<number | null>(null)
  const [addModal, setAddModal] = useState(false)

  //Table data -------------------------------------------------------------------------------
  const tableTitles = ['Variación', 'Precio', 'Descripción']
  const tableData: DataTableInterface[] =
    product?.variations.map((item) => ({
      rowId: item.id,
      payload: {
        Variación: (
          <div className="inline-flex items-center gap-2">
            <ImageComponent
              hash={item.image?.blurHash}
              src={item.image?.src}
              className="h-8 w-8 overflow-hidden rounded-full"
            />
            <p>{item.name}</p>
          </div>
        ),
        Precio: item?.price
          ? formatCurrency(item.price.amount, item.price.codeCurrency)
          : '-',
        Descripción: item?.description ?? '-',
      },
    })) ?? []

  const currentVariation: ServerVariationInterface | null = useMemo(() => {
    const current = product?.variations.find(
      (itm) => itm.id === detailModalData
    )
    if (current) {
      return current
    } else {
      return null
    }
  }, [product?.variations, detailModalData])

  const rowAction = (id: number) => {
    setDetailModalData(id)
  }

  const actions: BtnActions[] = []

  if (denyRoles(['AUDITOR'])) {
    actions.unshift({
      title: 'Nueva variación',
      action: () => setAddModal(true),
      icon: <PlusIcon className="h-5" />,
    })
  }

  //-------------------------------------------------------------------------------------------------

  return (
    <div className="h-[500px] overflow-scroll rounded-md border border-slate-300 p-5 pr-2 scrollbar-thin scrollbar-thumb-slate-200">
      <GenericTable
        tableTitles={tableTitles}
        tableData={tableData}
        rowAction={rowAction}
        actions={actions}
      />
      {!!currentVariation && (
        <Modal
          state={!!detailModalData}
          close={() => setDetailModalData(null)}
          size="m"
        >
          <VariationDetail
            variation={currentVariation}
            close={() => setDetailModalData(null)}
          />
        </Modal>
      )}
      {addModal && (
        <Modal state={addModal} close={setAddModal} size="m">
          <AddVariation close={() => setAddModal(false)} />
        </Modal>
      )}
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//DETAILS COMPONENT ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface DetailVariation {
  variation: ServerVariationInterface
  close: Function
}

const VariationDetail = ({ variation, close }: DetailVariation) => {
  const { denyRoles } = useServer()

  const { handleSubmit, control, formState, watch, unregister } = useForm()
  const { updateVariation, isFetching } = useServerProduct()
  const { deleteVariationProduct, isFetching: fetchingDelete } =
    useServerProduct()
  const { business } = useAppSelector((state) => state.init)
  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const [imageModal, setImageModal] = useState(false)

  const currenciesSelector =
    business?.availableCurrencies.map((itm) => itm.code) ?? []

  const [deleteAlert, setDeleteAlert] = useState(false)

  const addCallback = (variation: ServerVariationInterface) => {
    updateVariationState!(variation)
    close()
  }

  const enablePrice = watch('enablePrice')

  useEffect(() => {
    if (!enablePrice) unregister('price')
  }, [enablePrice])

  const onSubmit: SubmitHandler<Record<string, any>> = (data) => {
    formState.isDirty
      ? updateVariation(variation.id, data, addCallback)
      : close()
  }

  const deleteCallback = () => {
    updateVariationState && updateVariationState(variation, true)
    updateStockProductState &&
      updateStockProductState({
        ...product!,
        variation: product!.variations.filter((itm) => itm.id !== variation.id),
      })
    close()
  }

  return (
    <div className="">
      <div className="absolute top-5">
        <Button
          color="red-500"
          textColor="red-500"
          action={() => setDeleteAlert(true)}
          outline
          icon={<TrashIcon className="h-5" />}
        />
      </div>
      <h5 className="mt-5 text-lg">{variation.name}</h5>
      <div className="grid h-full grid-cols-10 gap-2 pt-2">
        <div className="group relative col-span-4 h-full rounded-md border border-gray-300 p-1">
          <ImageComponent
            className="h-full w-full"
            hash={variation?.image?.blurHash}
            src={variation?.image?.src}
          />
          <div className="absolute left-0 top-0 hidden h-full w-full items-center justify-center bg-gray-500 opacity-60 group-hover:flex">
            <p
              className="cursor-pointer font-normal text-white underline hover:text-lg"
              onClick={() => setImageModal(true)}
            >
              Cambiar imagen
            </p>
          </div>
        </div>
        <div className="col-span-6 rounded-md border border-gray-300 p-3">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex h-full flex-col"
          >
            <Toggle
              name="enablePrice"
              control={control}
              title="Habilitar precio"
              defaultValue={!!variation.price}
            />
            <div className="flex h-full flex-col">
              {(watch('enablePrice') ?? !!variation.price) && (
                <CurrencyAmountInput
                  currencies={currenciesSelector}
                  label="Precio"
                  name="price"
                  control={control}
                  defaultValue={variation.price}
                />
              )}
              <TextArea
                label="Descripción"
                name="description"
                control={control}
                defaultValue={variation.description}
              />
            </div>
            <div className="flex justify-end p-3">
              {denyRoles(['AUDITOR']) && (
                <Button
                  color="slate-600"
                  name={formState.isDirty ? 'Actualizar' : 'Aceptar'}
                  type="submit"
                  loading={isFetching}
                />
              )}
            </div>
          </form>
        </div>
      </div>
      {imageModal && (
        <Modal state={imageModal} close={setImageModal} size="m">
          <ChangeImage
            variation={variation}
            close={() => setImageModal(false)}
          />
        </Modal>
      )}

      {deleteAlert && (
        <Modal state={deleteAlert} close={setDeleteAlert}>
          <AlertContainer
            onAction={() =>
              deleteVariationProduct(variation.id, deleteCallback)
            }
            onCancel={() => setDeleteAlert(false)}
            title={`Eliminar variación "${product
              ?.attributes!.map((itm) => itm.value)
              .join(', ')}"`}
            text="Seguro desea continuar?"
            loading={fetchingDelete}
          />
        </Modal>
      )}
    </div>
  )
}
//++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//CHANGE IMAGE++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface Image {
  variation: ServerVariationInterface
  close: Function
}
const ChangeImage = ({ variation, close }: Image) => {
  const { denyRoles } = useServer()

  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const { updateVariation, isFetching } = useServerProduct()
  const availablesImages = product?.images
  const { handleSubmit, setValue, watch } = useForm()

  const updateCallback = (variation: ServerVariationInterface) => {
    const newVariations = product!.variations.filter(
      (itm) => itm.id !== variation.id
    )
    updateStockProductState &&
      updateStockProductState({
        ...product!,
        variations: [...newVariations, variation],
      })
    updateVariationState!(variation)
    close()
  }

  useEffect(() => {
    if (!!variation?.image) setValue('imageId', variation.image.id)
  }, [variation?.image])

  const onSubmit: SubmitHandler<Record<string, number>> = (data) => {
    updateVariation(variation.id, data, updateCallback)
  }

  const imageId = watch('imageId')
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="inline-flex w-full items-center justify-between py-3">
          <h5 className="text-lg text-gray-600">
            Seleccione la imagen deseada
          </h5>
          {imageId && (
            <Button
              color="red-500"
              textColor="red-500"
              outline
              name="Eliminar selección"
              icon={<TrashIcon className="h-5 text-red-500" />}
              action={() => setValue('imageId', null)}
            />
          )}
        </div>

        <div className="absolute right-10 top-5"></div>
        <div className="flex flex-wrap justify-center gap-3 pt-3">
          {availablesImages?.map((item, idx) => (
            <div key={idx} className="relative h-40 w-40">
              <input
                className="h-full w-full rounded-sm border-none bg-transparent checked:bg-none checked:text-transparent checked:ring-2 checked:ring-slate-500 active:ring-2 active:ring-slate-500"
                type="radio"
                value={item.id}
                onChange={(e) => setValue('imageId', item.id)}
                checked={imageId === item.id}
              />
              <ImageComponent
                hash={item?.blurHash}
                src={item?.src}
                className="absolute left-0 top-0 -z-10 h-full w-full"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end py-5">
          {denyRoles(['AUDITOR']) && (
            <Button
              name="Actualizar"
              color="slate-600"
              type="submit"
              loading={isFetching}
            />
          )}
        </div>
      </form>
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

//ADD COMPONENT++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
interface AddInterface {
  close: Function
}

const AddVariation = ({ close }: AddInterface) => {
  const {
    getProductAttributes,
    addVariation,
    productAttributes,
    isLoading,
    isFetching,
  } = useServerProduct()
  const { product, updateVariationState, updateStockProductState } =
    useContext(DetailProductContext)
  const { handleSubmit, control } = useForm()

  const onSubmit: SubmitHandler<Record<string, string>> = (data) => {
    if (Object.values(data).some((item) => !item)) {
      toast.warn('Debe asignarle un valor a todos los atributos')
    } else {
      addVariation &&
        addVariation(
          product!.id,
          data,
          close,
          updateVariationState!,
          updateStockProductState
        )
    }
  }

  useEffect(() => {
    getProductAttributes(product?.id!)
  }, [])

  if (isLoading)
    return (
      <SpinnerLoading className="flex h-48 flex-col items-center justify-center" />
    )

  return (
    <div>
      <h5 className="text-lg font-semibold text-gray-500">Nueva variación</h5>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`grid grid-cols-2 gap-3 pt-3`}>
          {productAttributes.map((item) => (
            <div
              key={item.code}
              className="flex flex-col items-center gap-y-2 rounded-md border border-gray-300 p-5"
            >
              <h5 className="text-md font-semibold text-gray-500">
                {item.name}
              </h5>
              <div className="flex">
                <InlineRadio
                  data={item.values.map((val) => ({
                    label: val.value,
                    value: val.value,
                  }))}
                  name={item.code}
                  control={control}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-3">
          <Button
            type="submit"
            color="slate-600"
            name="Insertar"
            loading={isFetching}
          />
        </div>
      </form>
    </div>
  )
}
//+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export default Variations
